import { CLAIM_STATUS, RESOLUTION_TYPE } from "../constants";
import type {
  Claim,
  ClaimTranslator,
  StatusViewModel,
  StepperStep,
} from "../types";
import {
  buildTimelineMap,
  getEffectiveResolution,
  isAwaitingReview,
} from "./claimState";
import { getHeroContent, getStatusAnnouncement } from "./hero";
import { getCustomerNextSteps } from "./nextSteps";
import { getResolutionSummary } from "./resolution";
import { getVisibleReturnLabel, isReturnRequest } from "./returnState";
import { getReturnStepperSteps } from "./returnStepper";
import { getStepperSteps } from "./stepper";

const getSteps = (
  claim: Claim,
  effectiveResolution: string,
  timelineMap: Record<string, string>,
  t: ClaimTranslator,
): StepperStep[] => {
  if (isReturnRequest(claim) && claim.claim_status !== CLAIM_STATUS.DENIED) {
    return getReturnStepperSteps(claim, timelineMap, t);
  }
  return getStepperSteps(claim, effectiveResolution, timelineMap, t);
};

/**
 * Maps a claim API response into the view model used by the status page.
 * Each section (hero, stepper, resolution, next steps) lives in its own module.
 */
export const getStatusViewModel = (
  claim: Claim,
  t: ClaimTranslator,
): StatusViewModel => {
  const effectiveResolution = getEffectiveResolution(claim);
  const timelineMap = buildTimelineMap(claim.timeline);
  const hero = getHeroContent(claim, t);

  return {
    hero,
    stepperSteps: getSteps(claim, effectiveResolution, timelineMap, t),
    resolution: getResolutionSummary(claim),
    effectiveResolution,
    showClaimItems: Boolean(claim.claim_items?.length),
    showShippingAddress: effectiveResolution === RESOLUTION_TYPE.RESHIP,
    nextSteps: getCustomerNextSteps(claim, t),
    notificationEmail: claim.email_address || null,
    isAwaitingReview: isAwaitingReview(claim),
    isReturnRequest: isReturnRequest(claim),
    returnLabel: getVisibleReturnLabel(claim),
    statusAnnouncement: getStatusAnnouncement(claim, hero, t),
  };
};
